import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Input, Space, Typography } from "antd";
import Select from "@/components/ResponsiveSelect";
import { AudioOutlined, StopOutlined } from "@ant-design/icons";
import moment from "moment";
import { prepareFacilityWorkorderDraft, transcribeFacilityAiAudio } from "../AiAssistant/api";
import useVoiceInput from "../AiAssistant/useVoiceInput";
import { DRAFT_FIELDS, draftValuesToApply } from "./draftValues.mjs";

const labels = { issue: "Issue", equipment: "Equipment", workorder_type: "Type", priority: "Priority", assignTo: "Assign To" };

export default function WorkorderDraftPanel({ companyId, facilityId, form, initialText = "", autoPrepare = false, onApplied, onBusyChange }) {
  const [text, setText] = useState(initialText);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const selectedEquipment = Form.useWatch("equipments", form);
  const selectedAssignee = Form.useWatch("assignTo", form);
  const controllerRef = useRef(null);
  const previousRef = useRef({});
  const callbacksRef = useRef({ onApplied, onBusyChange });
  callbacksRef.current = { onApplied, onBusyChange };
  const appendTranscript = useCallback((value) => setText((old) => `${old}${old ? " " : ""}${value}`), []);
  const transcribe = useCallback(({ blob, signal }) => transcribeFacilityAiAudio({ companyId, facilityId, blob, signal }).then((value) => value.text), [companyId, facilityId]);
  const voice = useVoiceInput({ onTranscript: appendTranscript, transcribe, enabled: !busy });

  const generate = useCallback(async (description) => {
    if (!description.trim() || controllerRef.current) return;
    const controller = new AbortController();
    controllerRef.current = controller;
    const snapshot = form.getFieldsValue(DRAFT_FIELDS);
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const draft = await prepareFacilityWorkorderDraft({ companyId, facilityId, text: description.trim(), signal: controller.signal });
      if (controller.signal.aborted) return;
      const values = draftValuesToApply({ draft, companyId, facilityId, current: form.getFieldsValue(DRAFT_FIELDS), snapshot, previous: previousRef.current });
      const formValues = { ...values };
      if (values.due_date) {
        const date = moment(values.due_date, "YYYY-MM-DD", true);
        if (date.isValid()) formValues.due_date = date;
        else delete formValues.due_date;
      }
      form.setFieldsValue(formValues);
      previousRef.current = { ...previousRef.current, ...values };
      callbacksRef.current.onApplied?.(formValues, draft.references);
      setResult(draft);
    } catch (failure) {
      if (!controller.signal.aborted) setError(failure?.response?.data?.detail || failure.message || "Could not prepare the draft. Please try again.");
    } finally {
      if (controllerRef.current === controller) controllerRef.current = null;
      if (!controller.signal.aborted) setBusy(false);
    }
  }, [companyId, facilityId, form]);

  useEffect(() => {
    if (autoPrepare && initialText.trim()) generate(initialText);
    return () => {
      controllerRef.current?.abort();
      controllerRef.current = null;
      callbacksRef.current.onBusyChange?.(false);
    };
  }, [autoPrepare, initialText, generate]);
  useEffect(() => {
    callbacksRef.current.onBusyChange?.(busy || voice.status !== "idle");
  }, [busy, voice.status]);

  const unresolved = Object.entries(result?.references || {}).filter(([, value]) => value.status !== "matched");
  return (
    <section aria-label="Describe a work order" style={{ marginBottom: 20, padding: 16, border: "1px solid #d9e7f5", borderRadius: 10, background: "#f7fbff" }}>
      <Typography.Title level={5} style={{ marginTop: 0 }}>Describe a work order</Typography.Title>
      <Typography.Paragraph type="secondary">Speak or type what needs attention. Review the filled fields, complete any missing details, then select Save.</Typography.Paragraph>
      <Input.TextArea aria-label="Work order description for AI" value={text} maxLength={12000} rows={3} disabled={busy || voice.status !== "idle"} onChange={(event) => setText(event.target.value)} placeholder="For example: AHU-02 is leaking on the second floor. High priority. Assign it to Alex." />
      <Space wrap style={{ marginTop: 10, marginBottom: 10 }}>
        <Button htmlType="button" aria-label={voice.status === "recording" ? "Stop recording" : "Start recording"} icon={voice.status === "recording" ? <StopOutlined /> : <AudioOutlined />} disabled={!voice.supported || busy || voice.status === "transcribing"} onClick={() => voice.status === "recording" ? voice.stop() : voice.start()}>
          {voice.status === "recording" ? "Stop recording" : voice.status === "transcribing" ? "Transcribing…" : "Start recording"}
        </Button>
        <Button htmlType="button" type="primary" loading={busy} disabled={!text.trim() || voice.status !== "idle"} onClick={() => generate(text)}>Fill draft</Button>
        {voice.status === "recording" && <span role="status">Recording · {Math.floor(voice.elapsedMs / 1000)}s</span>}
      </Space>
      {!voice.supported && <Typography.Paragraph type="secondary">Recording needs a supported browser on HTTPS or localhost. You can still type a description.</Typography.Paragraph>}
      {(error || voice.error) && <Alert type="error" showIcon message={error || voice.error} />}
      {result && <Alert type="info" showIcon message="Draft filled — review before saving. Fields you edited have been kept." description={
        <div>
          {result.missing_fields?.length > 0 && <div>Check required fields: {result.missing_fields.map((key) => labels[key] || key).join(", ")}.</div>}
          {unresolved.map(([key, reference]) => (
            <div key={key} style={{ marginTop: 8 }}>
              <div>{labels[key]}: “{reference.text}” {reference.status === "choose" ? "has possible matches. Select the correct entry." : "could not be matched. Select an entry in the form."}</div>
              {reference.status === "choose" && (
                <Select
                  aria-label={`Resolve ${labels[key]}`}
                  placeholder={`Select ${labels[key]}`}
                  style={{ width: "100%", maxWidth: 420, marginTop: 6 }}
                  value={key === "equipment" ? selectedEquipment?.[0] : selectedAssignee}
                  options={reference.options.map((option) => ({ value: option.id, label: option.label }))}
                  onChange={(value) => {
                    // equipment 表单字段已改多选（equipments 数组）
                    const patch = key === "equipment" ? { equipments: [value] } : { [key]: value };
                    form.setFieldsValue(patch);
                    callbacksRef.current.onApplied?.(patch, result.references);
                  }}
                />
              )}
            </div>
          ))}
          {result.clarifications?.map((note, index) => <div key={index}>{note}</div>)}
        </div>
      } />}
    </section>
  );
}
